import type {
  AccountAccessPayload,
  AccountPermissionNode,
  FeatureAccessResponse,
  OperatorAuthority
} from "@dealer-center/ddc-pages-configuration-interface";
import { useGetAccountAccess } from "./useGetAccountAccess";
import { useGetFeatureAccess } from "./useGetFeatureAccess";

/**
 * The permission code that opens the page (FR-001a).
 *
 * **The criterion is the permission, not internal status.** FR-001a says being internal "MUST NOT
 * be the criterion". Dealer callers happen to be blocked upstream today regardless — the BFF
 * rejects every non-internal caller before dispatch — but that is a rollout state, not this
 * design's behavior (D-14).
 *
 * ⚠️ **`"UserManager"`, CamelCase — NOT `"USER_MANAGER"`.** This is matched against
 * `EnrichedFeaturePermission.name`, which carries a **CC9 resource name** verbatim, and CC9's
 * resource names are CamelCase or dotted (`SpecialsBuilder`, `TradeMotion`,
 * `Inventory.FeeManager`) — **none** are SCREAMING_SNAKE. The normative list in this repo is the
 * BFF's `feature-group-map.ts`, transcribed from CC9's `Resources.java`, where the entry is
 * `UserManager: "OTHER"`.
 *
 * It shipped as `"USER_MANAGER"` and matched nothing, so `hasUserManagerPermission` was `false`
 * for **every** operator and the page denied access unconditionally (found 2026-09-14 against the
 * nonprod dev server; the constant had no live-data source — T001 was never run). The spec pins
 * the literal rather than reusing this constant, so the value itself is what regresses a change
 * back to snake case.
 *
 * The map cannot be imported here: it lives in the `lambda` layer and this is a `react`-layer lib,
 * so the two are separated by the module boundaries (which the linter does not currently enforce —
 * see `.claude/rules/code-style-eslint.md`).
 */
export const USER_MANAGER_PERMISSION_CODE = "UserManager";

export interface UseOperatorAuthorityOptions {
  /** The **operator's own** userId — the authenticated caller, never the selected subject. */
  operatorUserId?: string;
  accountId?: string;
  locale?: string;
}

/**
 * Flattens the account tree into the set of account ids the operator can reach.
 *
 * Recursive with no assumed depth: FR-039 makes the hierarchy arbitrarily deep, and
 * `AccountNode` already refuses to assume otherwise. `children` is optional and unvalidated, so a
 * missing `children` must read as "no children" and never throw — the same guard
 * `hasAccountHierarchy` makes, and for the same reason.
 */
const collectReachableAccountIds = (
  nodes: AccountPermissionNode[] | undefined,
  collected: Set<string>
): Set<string> => {
  (nodes ?? []).forEach(node => {
    /*
     * `writeAccess` is the granted flag for the OPERATOR's own record here, which is what makes
     * reading it legitimate — see FR-058's carve-out in the doc comment below. A node the operator
     * cannot write is not one they can grant to someone else.
     */
    if (node.writeAccess) {
      collected.add(node.accountId);
    }
    collectReachableAccountIds(node.children, collected);
  });

  return collected;
};

/**
 * What the person using the page may do (FR-058).
 *
 * **This is a deliberate, narrow carve-out, and the type is how the narrowness is enforced.** The
 * rule it carves out of is that feature-access data is display-only (FR-054): that rule exists
 * because the response describes the *requested* user, so it does not bind when the requested user
 * and the authenticated caller are **the same identity**. Both reads below are self-requests, which
 * is the entire justification — stated here at the point of use, as FR-058 requires.
 *
 * FR-058 also says the carve-out "MUST NOT be widened". `OperatorAuthority` carries **four**
 * members and nothing else, so a component holding one cannot widen it: raw `permissions[]` is
 * deliberately absent, because FR-046's question is a membership test and only the codes survive.
 *
 * **It takes TWO reads, not one.** FR-058b splits the grantable set in two, and the feature-access
 * record supplies only half:
 *
 * | Member | Comes from |
 * |---|---|
 * | `hasUserManagerPermission`, `isAccountAdministrator`, `grantedPermissionCodes` | the operator's **feature-access** self-read |
 * | `reachableAccountIds` | the operator's **account-access** self-read |
 *
 * FR-058's prose names only the feature-access record and reads as if one request covered both
 * halves; it does not (research R-005). Deriving account grantability from
 * `isAccountAdministrator` instead is forbidden in as many words: it "MUST NOT be evaluated against
 * … the caller's administrator flag alone".
 *
 * **Not a `useGet*` hook by naming, deliberately** (Principle V). It performs no request of its own
 * — it is a derivation over two read hooks that returns a purpose-built authority type, and
 * specifically **not** a `FeatureAccessResponse`. That distinction is SC-016's structural guard:
 * the subject's response and the operator's are the same shape from the same source, differing only
 * in whose they are (FR-058a), so the conflation is made impossible at **build time** rather than
 * caught at runtime.
 *
 * **Cache separation is free and already correct.** `useGetFeatureAccess`'s query key includes
 * `userId`, so the subject's read and the operator's are two entries and React Query cannot cross
 * them. When the operator selects **their own** record the two identifier pairs legitimately
 * coincide and collapse to one entry — correct, not a conflation.
 *
 * Sets are built with `new Set(...)` and read with `.has()` / `Array.from(...)`, **never**
 * `[...aSet]`: this lib typechecks at the ES5 default, where a spread over a `Set` is a TS2802
 * error and `test` would stay green while `typecheck` failed.
 */
export const useOperatorAuthority = ({
  operatorUserId,
  accountId,
  locale
}: UseOperatorAuthorityOptions) => {
  const featureAccess = useGetFeatureAccess({
    userId: operatorUserId,
    accountId,
    locale
  });
  const accountAccess = useGetAccountAccess({
    userId: operatorUserId,
    accountId
  });

  /*
   * ⚠️ **A DISABLED React Query reports `isPending: true` forever, and reading that as "still
   * loading" hangs the page.**
   *
   * Both reads are `enabled: Boolean(userId && accountId)`. With no operator identity — an absent
   * ControlCenter outlet context — neither ever runs, yet both sit at `status: "pending"` with
   * `fetchStatus: "idle"`. A consumer gating a spinner on `isPending` therefore shows it
   * indefinitely rather than reporting the wiring failure (found in review 2026-09-15; the spec
   * covering the no-context case passed only because it mocks this hook).
   *
   * So "pending" here means **enabled and not yet resolved**. Nothing is pending when there is
   * nothing to fetch.
   */
  const isEnabled = Boolean(operatorUserId && accountId);

  const isPending =
    isEnabled && (featureAccess.isPending || accountAccess.isPending);
  const isError = featureAccess.isError || accountAccess.isError;
  const isSuccess = featureAccess.isSuccess && accountAccess.isSuccess;

  const authority: OperatorAuthority | undefined = isSuccess
    ? toOperatorAuthority(featureAccess.data, accountAccess.data)
    : undefined;

  return {
    data: authority,
    isPending,
    isError,
    isSuccess,
    error: featureAccess.error ?? accountAccess.error,
    /**
     * FR-001a's page gate, resolved from the **feature-access read ALONE** — deliberately not from
     * `data`.
     *
     * `data` requires **both** reads (FR-058b splits the grantable sets across them), so gating the
     * page on `data` meant a failure of the *other* read denied access to an operator who holds the
     * permission. `accountaccess` is the slowest read on the page at ~5–6s and the likeliest to
     * fail or time out, and the denial it produced — "you don't have access" — is indistinguishable
     * from a real permission problem (found in review 2026-09-15).
     *
     * The permission is a fact about the feature-access response and nothing else, which is why it
     * can be answered earlier and more narrowly. When `accountaccess` fails, `data` stays
     * `undefined` and the permission tabs render read-only with FR-009's notice — the degradation
     * already designed for an absent authority — instead of the whole page turning into a denial.
     *
     * `hasPermission` is `undefined` until the read resolves, so a consumer cannot mistake
     * "not yet known" for "denied".
     */
    pageAccess: {
      hasPermission: featureAccess.data
        ? holdsUserManagerPermission(featureAccess.data)
        : undefined,
      isPending: isEnabled && featureAccess.isPending,
      isError: featureAccess.isError
    }
  };
};

/**
 * Whether the operator holds the permission that opens the page (FR-001a).
 *
 * Extracted so the page gate and `toOperatorAuthority` cannot disagree about what "holds the User
 * Manager permission" means — the two read it for different purposes and used to compute it in one
 * place only because the gate went through `OperatorAuthority`.
 *
 * Only **granted** rows count: the response's rows are the account catalog, so an ungranted row is
 * a permission the operator does not hold.
 */
export const holdsUserManagerPermission = (
  featureAccess: FeatureAccessResponse
) =>
  featureAccess.permissions.some(
    permission =>
      permission.granted && permission.name === USER_MANAGER_PERMISSION_CODE
  );

/**
 * The derivation itself, exported so it can be tested without rendering two queries.
 *
 * Only **granted** codes enter `grantedPermissionCodes`: the response's rows are the account
 * catalog rather than the operator's grants, so an ungranted row is a permission the operator does
 * **not** hold and must not be able to confer (FR-046, SC-004 — zero paths by which an operator can
 * grant a permission they do not themselves hold).
 */
export const toOperatorAuthority = (
  featureAccess: FeatureAccessResponse,
  accountAccess: AccountAccessPayload
): OperatorAuthority => {
  const grantedCodes = featureAccess.permissions
    .filter(permission => permission.granted)
    .map(permission => permission.name);

  return {
    // Shared with the page gate so the two cannot drift — see `holdsUserManagerPermission`.
    hasUserManagerPermission: holdsUserManagerPermission(featureAccess),
    isAccountAdministrator: featureAccess.administrator,
    grantedPermissionCodes: new Set(grantedCodes),
    reachableAccountIds: collectReachableAccountIds(
      accountAccess.accountPermissions,
      new Set<string>()
    )
  };
};
